import { InvoiceComplianceMode } from '../../enums/invoiceComplianceMode'
import { ZervantContactType } from '../../enums/zervantContactType'
import type { Invoice } from '../../types/invoices/invoice'
import type { MissingEInvoiceDetails } from '../../types/missingEInvoiceDetails'
import { getCompanyMissingFields } from '../getCompanyMissingFields'
import { getCustomerMissingFields } from '../getCustomerMissingFields'
import { getDefaultIbanPaymentFields } from '../getDefaultIbanPaymentFields'

interface GetMissingEInvoiceDetailsOptions {
  complianceMode: InvoiceComplianceMode
  invoice: Invoice | undefined
  isCompanyVatExempted: boolean
}

export const getMissingEInvoiceDetails = ({
  complianceMode,
  invoice,
  isCompanyVatExempted,
}: GetMissingEInvoiceDetailsOptions): MissingEInvoiceDetails => {
  const { company, customer } = invoice || {}
  const { businessId: registrationNo, legalFormSettings, legalStatus, vatNumber: vatNo } = company || {}
  const { address: customerAddress, customerType, eInvoiceAddress, isSalesTaxExempt, vatNumber } = customer || {}

  const { address: companyAddress, country: companyCountry } = company?.tradeName || {}
  const { city: companyCity, pobox: companyPobox, streetAddress1: companyStreet } = companyAddress || {}
  const { city: customerCity, country, pobox: customerPobox, streetAddress1: customerStreet } = customerAddress || {}

  const iban = getDefaultIbanPaymentFields(invoice?.paymentMeans)?.iban
  const sharedCapital = legalFormSettings?.sharedCapital
  const isSiretPendingAssignment = legalFormSettings?.isSiretPendingAssignment

  const {
    isMissingCity: isMissingCompanyCity,
    isMissingDetails: isMissingCompanyDetails,
    isMissingLegalStatus: isMissingCompanyLegalStatus,
    isMissingPaymentInfo: isMissingCompanyPaymentInfo,
    isMissingRegistrationNo: isMissingCompanyRegistrationNo,
    isMissingSharedCapital: isMissingCompanySharedCapital,
    isMissingStreet: isMissingCompanyStreet,
    isMissingTaxInfo: isMissingCompanyTaxInfo,
    isMissingVatNo: isMissingCompanyVatNo,
    isMissingZipCode: isMissingCompanyPobox,
  } = getCompanyMissingFields({
    city: companyCity,
    complianceMode,
    countryId: companyCountry,
    iban,
    isSiretPendingAssignment,
    isVatExempted: isCompanyVatExempted,
    legalStatus: legalStatus ?? undefined,
    registrationNo,
    sharedCapital,
    street: companyStreet,
    vatNo,
    zipCode: companyPobox,
  })

  const {
    isMissingCity: isMissingCustomerCity,
    isMissingDetails: isMissingCustomerBaseDetails,
    isMissingEInvoiceAddress: isMissingCustomerEInvoiceAddress,
    isMissingStreet: isMissingCustomerStreet,
    isMissingVatNumber: isMissingCustomerVatNumber,
    isMissingZipCode: isMissingCustomerPobox,
  } = getCustomerMissingFields({
    city: customerCity,
    complianceMode,
    country,
    eInvoiceAddress: eInvoiceAddress?.address,
    isIndividual: customerType === ZervantContactType.Person,
    isVatExempted: isSalesTaxExempt,
    street: customerStreet,
    vatNumber,
    zipCode: customerPobox,
  })

  // In French compliance mode, a missing e-invoicing address doesn't block sending on its own — the sender is
  // offered SendAsEInvoiceModal to fill it in whenever it's missing, instead of being sent here.
  const isCustomerEInvoiceAddressCollectedSeparately = complianceMode === InvoiceComplianceMode.French
  const isMissingCustomerDetails =
    isMissingCustomerBaseDetails || (!isCustomerEInvoiceAddressCollectedSeparately && isMissingCustomerEInvoiceAddress)
  const isMissingEInvoiceDetails = isMissingCompanyDetails || isMissingCustomerDetails

  return {
    companyDetails: {
      isMissingCompanyCity,
      isMissingCompanyLegalStatus,
      isMissingCompanyPaymentInfo,
      isMissingCompanyPobox,
      isMissingCompanyRegistrationNo,
      isMissingCompanySharedCapital,
      isMissingCompanyStreet,
      isMissingCompanyTaxInfo,
      isMissingCompanyVatNo,
    },
    customerDetails: {
      isMissingCustomerCity,
      isMissingCustomerEInvoiceAddress,
      isMissingCustomerPobox,
      isMissingCustomerStreet,
      isMissingCustomerVatNumber,
    },
    isMissingCompanyDetails,
    isMissingCustomerDetails,
    isMissingEInvoiceDetails,
  }
}
